import React, { useState, useEffect } from 'react';
import {
  Server, Play, Square, RotateCcw, Trash2, RefreshCw,
  CheckCircle, XCircle, AlertTriangle, Database, Activity,
  Lock, KeyRound, Eye, Import
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';
import apiService from '../services/api';

const ClusterManager = ({ onSelectCluster }) => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterStatus, setClusterStatus] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    privateKey: '',
    authType: 'password'
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const [importData, setImportData] = useState({
    name: '',
    hosts: '',
    version: '3.6.1',
    mode: 'KRaft',
    installPath: '/opt/kafka',
    dataPath: '/var/lib/kafka'
  });

  useEffect(() => {
    loadClusters();
  }, []);

  const loadClusters = async () => {
    try {
      setLoading(true);
      const response = await apiService.getClusters();
      if (response.success) {
        setClusters(response.clusters);
      }
    } catch (err) {
      setError('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCluster = async (cluster) => {
    setSelectedCluster(cluster);
    setClusterStatus(null);
    const savedCreds = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (savedCreds) {
      const creds = JSON.parse(savedCreds);
      setCredentials(creds);
      await loadClusterStatus(cluster.id, creds);
    } else {
      setPendingAction({ type: 'status', cluster });
      setShowCredentialsDialog(true);
    }
  };

  const loadClusterStatus = async (clusterId, creds) => {
    try {
      setActionLoading(true);
      setError(null);
      const response = await apiService.getClusterStatus(clusterId, creds);
      if (response.success) {
        setClusterStatus(response);
      }
    } catch (err) {
      setError('Failed to get cluster status: ' + (err.message || 'Check credentials'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleNodeAction = async (nodeHost, action) => {
    setPendingAction({ type: 'nodeAction', nodeHost, action });
    const savedCreds = localStorage.getItem(`cluster_creds_${selectedCluster.id}`);
    if (savedCreds) {
      await executeNodeAction(nodeHost, action, JSON.parse(savedCreds));
    } else {
      setShowCredentialsDialog(true);
    }
  };

  const executeNodeAction = async (nodeHost, action, creds) => {
    try {
      setActionLoading(true);
      setError(null);
      await apiService.nodeAction(selectedCluster.id, nodeHost, action, creds);
      await loadClusterStatus(selectedCluster.id, creds);
    } catch (err) {
      setError(`Failed to ${action} node: ` + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCredentialsSubmit = async () => {
    const creds = {
      username: credentials.username,
      password: credentials.authType === 'password' ? credentials.password : undefined,
      privateKey: credentials.authType === 'key' ? credentials.privateKey : undefined
    };

    const clusterId = selectedCluster?.id || pendingAction?.cluster?.id;
    if (clusterId) {
      localStorage.setItem(`cluster_creds_${clusterId}`, JSON.stringify(credentials));
    }

    setShowCredentialsDialog(false);

    if (pendingAction?.type === 'status') {
      setSelectedCluster(pendingAction.cluster);
      await loadClusterStatus(pendingAction.cluster.id, creds);
    } else if (pendingAction?.type === 'nodeAction') {
      await executeNodeAction(pendingAction.nodeHost, pendingAction.action, creds);
    }
    
    setPendingAction(null);
  };

  const handleDeleteCluster = async (clusterId) => {
    if (!window.confirm('Remove this cluster from saved list?')) return;
    
    try {
      await apiService.deleteCluster(clusterId);
      localStorage.removeItem(`cluster_creds_${clusterId}`);
      loadClusters();
      if (selectedCluster?.id === clusterId) {
        setSelectedCluster(null);
        setClusterStatus(null);
      }
    } catch (err) {
      setError('Failed to delete cluster');
    }
  };

  const handleImportCluster = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const hostList = importData.hosts
        .split(/[,\n]/)
        .map(h => h.trim())
        .filter(h => h);

      if (hostList.length === 0) {
        setError('Please enter at least one host');
        return;
      }

      const clusterData = {
        name: importData.name || `Imported-Cluster-${Date.now()}`,
        nodes: hostList.map((host, i) => ({
          host: host,
          privateIp: host,
          brokerId: i
        })),
        version: importData.version,
        mode: importData.mode,
        installPath: importData.installPath,
        dataPath: importData.dataPath,
        imported: true
      };

      await apiService.saveCluster(clusterData);
      setShowImportDialog(false);
      setImportData({
        name: '',
        hosts: '',
        version: '3.6.1',
        mode: 'KRaft',
        installPath: '/opt/kafka',
        dataPath: '/var/lib/kafka'
      });
      loadClusters();
    } catch (err) {
      setError('Failed to import cluster: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="success" className="text-xs"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>;
      case 'degraded':
        return <Badge variant="warning" className="text-xs"><AlertTriangle className="h-3 w-3 mr-1" />Degraded</Badge>;
      case 'down':
        return <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" />Down</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">Unknown</Badge>;
    }
  };

  const getNodeStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'stopped':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          <h2 className="text-base font-bold">Saved Clusters</h2>
          <Badge variant="outline" className="text-xs">{clusters.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)} className="h-8 text-xs">
            <Import className="h-4 w-4 mr-1" />
            Import Cluster
          </Button>
          <Button variant="outline" size="sm" onClick={loadClusters} disabled={loading} className="h-8 text-xs">
            <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs ml-2">{error}</AlertDescription>
          <Button variant="ghost" size="sm" className="ml-auto h-6 px-2" onClick={() => setError(null)}>×</Button>
        </Alert>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          {clusters.length === 0 ? (
            <Card className="p-6 text-center">
              <Database className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No clusters saved yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Deploy a cluster or import an existing one
              </p>
              <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)} className="mt-3 h-8 text-xs">
                <Import className="h-4 w-4 mr-1" />
                Import Existing Cluster
              </Button>
            </Card>
          ) : (
            clusters.map(cluster => (
              <Card
                key={cluster.id}
                className={cn(
                  "p-3 cursor-pointer transition-all hover:border-primary/50",
                  selectedCluster?.id === cluster.id && "border-primary bg-primary/5"
                )}
                onClick={() => handleSelectCluster(cluster)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-primary" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{cluster.name}</p>
                        {cluster.imported && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1">Imported</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {cluster.nodes?.length || 0} nodes • {cluster.mode} • v{cluster.version}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectCluster && onSelectCluster(cluster);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCluster(cluster.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <div className="col-span-2">
          {selectedCluster ? (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold">{selectedCluster.name}</h3>
                    {selectedCluster.imported && (
                      <Badge variant="outline" className="text-xs">Imported</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedCluster.mode} Mode • Kafka {selectedCluster.version}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {clusterStatus && getStatusBadge(clusterStatus.cluster.status)}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const savedCreds = localStorage.getItem(`cluster_creds_${selectedCluster.id}`);
                      if (savedCreds) {
                        loadClusterStatus(selectedCluster.id, JSON.parse(savedCreds));
                      } else {
                        setPendingAction({ type: 'status', cluster: selectedCluster });
                        setShowCredentialsDialog(true);
                      }
                    }}
                    disabled={actionLoading}
                    className="h-8 text-xs"
                  >
                    <RefreshCw className={cn("h-4 w-4 mr-1", actionLoading && "animate-spin")} />
                    Refresh Status
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onSelectCluster && onSelectCluster(selectedCluster)}
                    className="h-8 text-xs"
                  >
                    <Activity className="h-4 w-4 mr-1" />
                    Manage Topics
                  </Button>
                </div>
              </div>

              <Separator className="mb-4" />

              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nodes</h4>
                
                {clusterStatus?.nodes ? (
                  <div className="space-y-2">
                    {clusterStatus.nodes.map((node) => (
                      <div
                        key={node.host}
                        className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          {getNodeStatusIcon(node.kafka)}
                          <div>
                            <p className="text-sm font-medium">
                              Broker {node.brokerId} - {node.host}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {node.privateIp && node.privateIp !== node.host && `Private: ${node.privateIp} • `}
                              Kafka: {node.kafka}
                              {node.zookeeper !== 'n/a' && ` • ZK: ${node.zookeeper}`}
                              {node.error && <span className="text-red-400"> • {node.error}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs px-2"
                            onClick={() => handleNodeAction(node.host, 'start')}
                            disabled={actionLoading || node.kafka === 'running'}
                          >
                            <Play className="h-3 w-3 mr-1" />
                            Start
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs px-2"
                            onClick={() => handleNodeAction(node.host, 'stop')}
                            disabled={actionLoading || node.kafka === 'stopped'}
                          >
                            <Square className="h-3 w-3 mr-1" />
                            Stop
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs px-2"
                            onClick={() => handleNodeAction(node.host, 'restart')}
                            disabled={actionLoading}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restart
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Activity className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {actionLoading ? 'Loading status...' : 'Click "Refresh Status" to check node health'}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-6 text-center">
              <Server className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Select a cluster to view details</p>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">SSH Credentials</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-xs text-muted-foreground">
              Enter SSH credentials to connect to cluster nodes
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="ec2-user"
                className="h-9 text-sm"
              />
            </div>
            <Tabs
              value={credentials.authType}
              onValueChange={(v) => setCredentials({ ...credentials, authType: v })}
            >
              <TabsList className="h-8 grid w-full grid-cols-2 bg-muted/50">
                <TabsTrigger value="password" className="text-xs h-7">
                  <Lock className="h-3.5 w-3.5 mr-1" />
                  Password
                </TabsTrigger>
                <TabsTrigger value="key" className="text-xs h-7">
                  <KeyRound className="h-3.5 w-3.5 mr-1" />
                  SSH Key
                </TabsTrigger>
              </TabsList>
              <TabsContent value="password" className="mt-3">
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Enter password"
                  className="h-9 text-sm"
                />
              </TabsContent>
              <TabsContent value="key" className="mt-3">
                <Textarea
                  value={credentials.privateKey}
                  onChange={(e) => setCredentials({ ...credentials, privateKey: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  rows={5}
                  className="text-xs font-mono"
                />
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)} size="sm" className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleCredentialsSubmit} size="sm" className="h-8 text-xs">
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Import className="h-4 w-4" />
              Import Existing Cluster
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-xs text-muted-foreground">
              Add an existing Kafka cluster that was installed outside this tool
            </p>
            
            <div className="space-y-1.5">
              <Label className="text-xs">Cluster Name</Label>
              <Input
                value={importData.name}
                onChange={(e) => setImportData({ ...importData, name: e.target.value })}
                placeholder="Production Kafka Cluster"
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Broker Hosts (comma or newline separated)</Label>
              <Textarea
                value={importData.hosts}
                onChange={(e) => setImportData({ ...importData, hosts: e.target.value })}
                placeholder="broker1.example.com&#10;broker2.example.com&#10;broker3.example.com"
                rows={3}
                className="text-xs font-mono"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Kafka Version</Label>
                <Input
                  value={importData.version}
                  onChange={(e) => setImportData({ ...importData, version: e.target.value })}
                  placeholder="3.6.1"
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mode</Label>
                <Select value={importData.mode} onValueChange={(v) => setImportData({ ...importData, mode: v })}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KRaft" className="text-xs">KRaft</SelectItem>
                    <SelectItem value="Legacy" className="text-xs">Legacy (ZooKeeper)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Kafka Install Path</Label>
              <Input
                value={importData.installPath}
                onChange={(e) => setImportData({ ...importData, installPath: e.target.value })}
                placeholder="/opt/kafka"
                className="h-9 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)} size="sm" className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleImportCluster} disabled={actionLoading || !importData.hosts} size="sm" className="h-8 text-xs">
              {actionLoading ? 'Importing...' : 'Import Cluster'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClusterManager;
